import {
  AfterViewInit,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Input,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { activeTranslations } from '../../enum/constants';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  private root!: am5.Root;

  @Input() days: string[] = [];
  @Input() openChatValues: number[] = [];
  @Input() closedChatValues: number[] = [];
  @Input() reOpenChatValues: number[] = [];
  @Input() tooltipText: string = '{category}: {valueY}';
  @Input() extraMax!: number;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
  ) {}

  ngAfterViewInit() {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['openChatValues'] ||
      changes['closedChatValues'] ||
      changes['reOpenChatValues'] ||
      changes['extraMax']
    ) {
      this.updateChart();
    }
  }

  createChart() {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        this.root = am5.Root.new('chartDiv');

        this.root._logo?.dispose();

        this.root.setThemes([am5themes_Animated.new(this.root)]);

        const chart = this.root.container.children.push(
          am5xy.XYChart.new(this.root, {
            panY: false,
            layout: this.root.verticalLayout,
          }),
        );

        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(this.root, {
            renderer: am5xy.AxisRendererY.new(this.root, {}),
            min: 0,
            max: this.extraMax || 5,
          }),
        );

        const xAxis = chart.xAxes.push(
          am5xy.CategoryAxis.new(this.root, {
            renderer: am5xy.AxisRendererX.new(this.root, {}),
            categoryField: 'category',
          }),
        );

        xAxis.data.setAll(this.getData());

        const openSeries = chart.series.push(
          am5xy.LineSeries.new(this.root, {
            name: activeTranslations.opened_chats,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'openValue',
            categoryXField: 'category',
            fill: am5.color(0xff5733),
            stroke: am5.color(0xff5733),
          }),
        );

        openSeries.data.setAll(this.getData());

        openSeries.set(
          'tooltip',
          am5.Tooltip.new(this.root, {
            labelText: this.tooltipText,
          }),
        );

        const closedSeries = chart.series.push(
          am5xy.LineSeries.new(this.root, {
            name: activeTranslations.closed_chats,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'closedValue',
            categoryXField: 'category',
            fill: am5.color(0x00ff00),
            stroke: am5.color(0x00ff00),
          }),
        );

        closedSeries.data.setAll(this.getData());

        closedSeries.set(
          'tooltip',
          am5.Tooltip.new(this.root, {
            labelText: this.tooltipText,
          }),
        );

        const reOpenSeries = chart.series.push(
          am5xy.LineSeries.new(this.root, {
            name: activeTranslations.reopened_chats,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'reOpenValue',
            categoryXField: 'category',
            fill: am5.color(0x0000ff),
            stroke: am5.color(0x0000ff),
          }),
        );

        reOpenSeries.data.setAll(this.getData());

        reOpenSeries.set(
          'tooltip',
          am5.Tooltip.new(this.root, {
            labelText: this.tooltipText,
          }),
        );

        const legend = chart.children.push(am5.Legend.new(this.root, {}));
        legend.data.setAll(chart.series.values);

        chart.set('scrollbarX', am5.Scrollbar.new(this.root, { orientation: 'horizontal' }));
        chart.set('cursor', am5xy.XYCursor.new(this.root, {}));
      });
    }
  }

  updateChart() {
    if (this.root) {
      const chart = this.root.container.children.getIndex(0) as am5xy.XYChart;
      if (chart) {
        const yAxis = chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRenderer>;
        if (yAxis) {
          yAxis.set('max', this.extraMax);
        }

        const xAxis = chart.xAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRenderer>;
        if (xAxis) {
          xAxis.data.setAll(this.getData());
        }

        const openSeries = chart.series.getIndex(0) as am5xy.LineSeries;
        if (openSeries) {
          openSeries.data.setAll(this.getData());
          openSeries.set(
            'tooltip',
            am5.Tooltip.new(this.root, {
              labelText: this.tooltipText,
            }),
          );
        }
        const closedSeries = chart.series.getIndex(1) as am5xy.LineSeries;
        if (closedSeries) {
          closedSeries.data.setAll(this.getData());
          closedSeries.set(
            'tooltip',
            am5.Tooltip.new(this.root, {
              labelText: this.tooltipText,
            }),
          );
        }
        const reOpenChatValues = chart.series.getIndex(2) as am5xy.LineSeries;
        if (reOpenChatValues) {
          reOpenChatValues.data.setAll(this.getData());
          reOpenChatValues.set(
            'tooltip',
            am5.Tooltip.new(this.root, {
              labelText: this.tooltipText,
            }),
          );
        }
      }
    }
  }

  getData() {
    return this.days.map((day, index) => ({
      category: day,
      openValue: this.openChatValues[index] || 0,
      closedValue: this.closedChatValues[index] || 0,
      reOpenValue: this.reOpenChatValues[index] || 0,
    }));
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose();
    }
  }
}
